import memoizeOne from 'memoize-one';
import { ff } from '@atlassian/jira-feature-flagging';
import { ITEM_TYPE, CHILD_ITEM_TYPE, CREATE_ITEM_TYPE } from '../../../constants';
import type { TOP, BOTTOM, CreatePosition } from '../../../context';
import type { EnrichedCreateItem, FlatItem, FlattenedEnrichedItem, ItemId } from '../../../types';
import { isBaseLevel, isRoot } from '../../../utils/hierarchy-enriched-items';
import type { RenderItem, RenderCreateItem, RenderItemContainer } from './types';

// Remove the following during jwm.visual-hierarchy-milestone-1 cleanup
export const isParentCreateItem = (item: FlattenedEnrichedItem) =>
	item.type === CREATE_ITEM_TYPE && !isBaseLevel(item.meta.level);

// this function is only called when 'jwm.visual-hierarchy-milestone-1' is off
// and, it should be removed once we clean up the ff
export const getChildItem = (
	childItem: FlattenedEnrichedItem,
	isVirtual: boolean,
	renderItem: RenderItem,
	renderItemContainer: RenderItemContainer,
	renderCreateItem: RenderCreateItem,
) => {
	if (childItem.type === CREATE_ITEM_TYPE && isBaseLevel(childItem.meta.level)) {
		return renderCreateItem(childItem.anchorItem, childItem.meta);
	}
	if (childItem.type === CHILD_ITEM_TYPE) {
		const { meta, item, flatIndex } = childItem;
		const child = renderItem(item, meta, flatIndex, isVirtual);

		const childItemContainerMeta = {
			parentId: meta.parentId,
			isExpanded: meta.isExpanded,
			isParent: meta.isParent,
			id: item.id,
			level: item.level,
			ariaRowIndex: meta.ariaRowIndex,
			startOffset: meta.startOffset,
			parentStartOffset: meta.parentStartOffset,
			isInTransition: item.isInTransition,
		};

		return renderItemContainer(childItemContainerMeta, childItem.flatIndex, child);
	}

	// this will never happen, item type check was necessary for union item type
	return undefined;
};

/**
 * Return the create meta if the child container is visible and child create item is not visible.
 * Return undefined
 *  when the create-item is visible OR;
 *      create-item is not a child create OR;
 *      the parent container is not visible.
 */
export const getChildCreateMetaIfContainerVisible = memoizeOne(
	(
		createPosition: CreatePosition,
		enrichedCreateItem: EnrichedCreateItem<FlatItem> | undefined,
		visibleItems: FlattenedEnrichedItem[],
	):
		| {
				position: typeof TOP | typeof BOTTOM;
				parentId: ItemId;
		  }
		| undefined => {
		const isCreateItemNested =
			enrichedCreateItem &&
			(ff('jwm.visual-hierarchy-milestone-1')
				? !isRoot(enrichedCreateItem?.meta.depth ?? 0)
				: isBaseLevel(enrichedCreateItem.meta.level));
		if (
			createPosition !== undefined &&
			enrichedCreateItem?.type === CREATE_ITEM_TYPE &&
			enrichedCreateItem?.anchorItem &&
			isCreateItemNested
		) {
			// anchor item is not the parent, it could be the next child issue

			const { anchorItem, meta } = enrichedCreateItem;
			let parentId: string | undefined;

			if (
				ff('jwm.visual-hierarchy-milestone-1')
					? isRoot(anchorItem.depth ?? 0)
					: !isBaseLevel(anchorItem.level)
			) {
				// create child last
				parentId = anchorItem.id;
			} else {
				// create inline child
				parentId = meta.parentId;
			}
			const isAnchorChildItemVisible = visibleItems.find(
				(visibleItem) => visibleItem.type === ITEM_TYPE && visibleItem.item?.id === parentId,
			);

			if (isAnchorChildItemVisible && parentId) {
				return { position: createPosition, parentId };
			}
		}

		return undefined;
	},
);
